import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box } from '@atlaskit/primitives/compiled';
import { SmartLinkSize } from '@atlaskit/smart-card';
import { CardSSR as SmartCardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';
import {
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';

import {
	onNonGenerativeAIAnswerResultShown,
	SmartLinksAnalyticsContextLocation,
} from '../../../ai-answer-dialog/analytics';
import type {
	commonAttributesType,
	NonGenerativeAIAnswerResultShownProps,
} from '../../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { getLinkTarget } from '../../../common/utils/get-link-target';
import { resolveJiraUrl } from '../../../common/utils/resolve-jira-url';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import { useDwellTracker } from '../../../common/utils/use-dwell-tracker';
import {
	type AiBrand,
	AiExperienceName,
	AnswerCardType,
	DEFAULT_GAS_CHANNEL,
} from '../../../constants';
import { onNonGenerativeKnowledgeCardLinkClick } from '../../../knowledge-cards/analytics';
import { isHello, sha256Hash } from '../../../utils';
import { AIDisclaimerAndFeedback } from '../../common/ui/ai-disclaimer-and-feedback';

const styles = cssMap({
	root: {
		backgroundColor: token('color.background.neutral.subtle'),
		marginBottom: token('space.025'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		width: '100%',
	},
});

export const JiraIssueSmartCardComponent = ({
	issueKeyQuery,
	cloudId,
	aiBrand,
	searchSessionId,
	workspaceId,
	product,
}: {
	issueKeyQuery: string;
	cloudId: string;
	aiBrand: AiBrand;
	searchSessionId: string;
	workspaceId: string;
	product: string;
}): React.JSX.Element | null => {
	const [issueUrl, setIssueUrl] = useState<string | null>(null);
	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const cardRef = useRef<HTMLDivElement>(null);
	const initialLoadTs = useRef<number>(performance.now()).current;

	const analyticsAttributes = useMemo<NonGenerativeAIAnswerResultShownProps>(
		() => ({
			searchSessionId,
			queryHash: sha256Hash(issueKeyQuery),
			brand: aiBrand,
			source: 'jira_issue_card',
			query: issueKeyQuery,
			answerString: issueUrl || '',
			answerCardType: AnswerCardType.JIRA_ISSUE,
		}),
		[searchSessionId, issueKeyQuery, aiBrand, issueUrl],
	);

	const analyticsAttributesWithoutUGC = useMemo<
		Omit<NonGenerativeAIAnswerResultShownProps, 'query' | 'answerString'>
	>(
		() => ({
			searchSessionId,
			queryHash: sha256Hash(issueKeyQuery),
			brand: aiBrand,
			source: 'jira_issue_card',
			answerCardType: AnswerCardType.JIRA_ISSUE,
		}),
		[searchSessionId, issueKeyQuery, aiBrand],
	);

	const commonAttributes = useMemo<commonAttributesType>(
		() => ({
			cloudId,
			orgId: '', // Not available in this component
			source: 'jira_issue_card',
			query: issueKeyQuery,
			queryHash: sha256Hash(issueKeyQuery),
			baseQueryHash: sha256Hash(issueKeyQuery),
			searchSessionId,
			answerString: issueUrl || '',
			answerLength: issueUrl?.length || 0,
			answerWordCount: 0,
			answerFormat: null,
			answerCardType: AnswerCardType.JIRA_ISSUE,
			sources: [],
			followUps: [],
			errorState: null,
			errorStateSuggestedQueries: [],
			extraAttributes: {
				issueKey: issueKeyQuery,
			},
			apiSource: 'xp-knowledge-discovery',
			workspaceId,
			sessionId: searchSessionId,
			brand: aiBrand,
			isHello: isHello(cloudId),
		}),
		[cloudId, issueKeyQuery, searchSessionId, issueUrl, workspaceId, aiBrand],
	);

	// Dwell tracker for viewport exit detection
	useDwellTracker({
		elementRef: cardRef,
		onDwellEnd: (dwellTime) => {
			fireSAINUsageEvent({
				metCriteria: SainAction.FMP_RESULT_EXITED,
				dwellTime,
				commonAttributes,
			});
		},
	});

	useEffect(() => {
		resolveJiraUrl({ issueKey: issueKeyQuery, cloudId, workspaceId, product }).then(setIssueUrl);
	}, [issueKeyQuery, cloudId, workspaceId, product]);

	useEffect(() => {
		if (issueUrl) {
			const fmpLoadTime = Math.round(performance.now() - initialLoadTs);
			trackAIInteractionInit();

			fireAnalyticsEvent(onNonGenerativeAIAnswerResultShown({ ...commonAttributes, fmpLoadTime }));
			SAINFeatureControl.triggerNonPrimaryClickTrackingExposure();
			fireSAINUsageEvent({});
		}
	}, [
		fireAnalyticsEvent,
		fireSAINUsageEvent,
		analyticsAttributes,
		commonAttributes,
		issueUrl,
		trackAIInteractionInit,
		initialLoadTs,
	]);

	if (!issueUrl) {
		return null;
	}

	const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
		const isAuxClick = 'button' in event && event.button === 1;
		const isContextMenu = 'button' in event && event.button === 2;
		if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
			return;
		}

		fireAnalyticsEvent(
			onNonGenerativeKnowledgeCardLinkClick({ ...analyticsAttributes, isAuxClick, isContextMenu }),
		);

		// Passing a click handler to the `SmartCardSSR` component requires navigation handling here
		if (!isAuxClick && !isContextMenu) {
			const target = getLinkTarget(event);
			window.open(issueUrl, target);
		}
	};

	return (
		<Fragment>
			<Box ref={cardRef} xcss={styles.root} onAuxClick={(e: React.MouseEvent) => { if (e.button !== 1) { return; } handleClick(e); }} onContextMenu={handleClick}>
				<AnalyticsContext
					data={{
						attributes: {
							location: SmartLinksAnalyticsContextLocation.SAIN_JIRA_ISSUE_CARD,
							...analyticsAttributesWithoutUGC,
						},
						location: SmartLinksAnalyticsContextLocation.SAIN_JIRA_ISSUE_CARD,
					}}
				>
					<SmartCardProvider>
						<SmartCardSSR
							url={issueUrl}
							appearance="block"
							ui={{ size: SmartLinkSize.Medium }}
							onClick={handleClick}
						/>
					</SmartCardProvider>
				</AnalyticsContext>
			</Box>
			<AIDisclaimerAndFeedback showFeedbackButtons />
		</Fragment>
	);
};

export const JiraIssueSmartCard = (props: {
	issueKeyQuery: string;
	cloudId: string;
	aiBrand: AiBrand;
	searchSessionId: string;
	workspaceId: string;
	product: string;
	subproduct?: string;
	source: string;
}): React.JSX.Element => (
	<AIEventsInstrumentationProvider
		config={{
			product: props.product,
			subproduct: props.subproduct ?? 'unknown',
			aiFeatureName: 'smartAnswers',
			aiExperienceName: AiExperienceName.JIRA_ISSUE_CARD,
			proactiveGeneratedAI: 0,
			userGeneratedAI: 1,
			isAIFeature: 0,
			source: props.source,
			channel: DEFAULT_GAS_CHANNEL,
		}}
	>
		<JiraIssueSmartCardComponent {...props} />
	</AIEventsInstrumentationProvider>
);
